import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { Loader } from "components/Custom/Loader";
import FullScreenLoader from "components/Custom/FullScreenLoader";
import { popUp, saveMsg } from "components/Custom/AreYouSurePopUp";
import { UserContext, notify } from "App";
import {
  GetDevoteeDataAPI,
  GetDevoteeAddressAPI,
  EditDevoteDataAPI,
  EditDevoteeAddressAPI,
} from "APIs/DevoteeAPIs";
// Receipt page theme (shared)
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SectionCard, {
  SectionHeader,
  ranuButtonSx,
  ranuOutlinedButtonSx,
} from "layouts/receipt/components/SectionCard";
import EditDevoteeDetails from "./editDevoteeDetails";
import EditDevoteeAddress, { ADDRESS_FIELDS as ADDRESS_FORM_FIELDS } from "./editDevoteeAddress";

/* -------------------------------------------------------------------------- */
/* Validation rules                                                           */
/* -------------------------------------------------------------------------- */
const REQUIRED_DEVOTEE_FIELDS = ["first_name", "last_name"];

// Date fields are optional, but must not be in the future
const DATE_FIELDS = ["birth_date", "anniversary_date"];

// Every devotee field that gets validated (required + format checks)
const VALIDATED_DEVOTEE_FIELDS = [...REQUIRED_DEVOTEE_FIELDS, ...DATE_FIELDS];

// Derived from the address form config, so the two can never drift apart
const ADDRESS_FIELD_NAMES = ADDRESS_FORM_FIELDS.map((field) => field.name);
const REQUIRED_ADDRESS_FIELDS = ADDRESS_FORM_FIELDS.filter((f) => f.required).map((f) => f.name);

// Today's date as yyyy-mm-dd in the user's LOCAL time zone
const getTodayISO = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
};

// Accepts dd-mm-yyyy or yyyy-mm-dd and returns yyyy-mm-dd
const toISODate = (value) => {
  const parts = String(value).split("-");
  return parts[0].length === 4 ? parts.join("-") : parts.reverse().join("-");
};

const isNotFutureDate = (value) => toISODate(value) <= getTodayISO();

// Format rules, checked only when the field has a value
const VALIDATORS = {
  phone: {
    isValid: (v) => v.length === 10,
    message: "Phone number must be exactly 10 digits",
  },
  email: {
    isValid: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
    message: "Please enter a valid email address",
  },
  birth_date: {
    isValid: isNotFutureDate,
    message: "Birth date cannot be in the future",
  },
  anniversary_date: {
    isValid: isNotFutureDate,
    message: "Anniversary date cannot be in the future",
  },
};

const labelOf = (name) => name.split("_").join(" ");

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

// gender uses 0 / "0" for Male, so check for "empty" explicitly instead of using falsy
const isEmpty = (value) => value === "" || value === null || value === undefined;

// Returns an error message, or "" when the value is valid
const getFieldError = (name, value, required) => {
  if (required && (isEmpty(value) || String(value).trim() === "")) {
    return `${capitalize(labelOf(name))} is required`;
  }

  const rule = VALIDATORS[name];
  if (rule && !isEmpty(value) && !rule.isValid(String(value))) {
    return rule.message;
  }

  return "";
};

// Digits only, max 10 characters
const sanitizePhone = (value) => `${value ?? ""}`.replace(/\D/g, "").slice(0, 10);

// Keys used by the "touched" state
const devoteeTouchKey = (name) => `devotee:${name}`;
const addressTouchKey = (address, name) => `address:${address.devotee_address_id}:${name}`;

const getDevoteeFieldError = (devotee, name) =>
  getFieldError(name, devotee?.[name], REQUIRED_DEVOTEE_FIELDS.includes(name));

const getAddressFieldError = (address, name) =>
  getFieldError(name, address[name], REQUIRED_ADDRESS_FIELDS.includes(name));

// Fields that are already invalid when the form loads are marked as touched,
// so their errors are visible straight away instead of waiting for a blur.
const getInitialTouched = (devotee, addresses) => {
  const touched = {};

  VALIDATED_DEVOTEE_FIELDS.forEach((name) => {
    if (getDevoteeFieldError(devotee, name)) touched[devoteeTouchKey(name)] = true;
  });

  addresses.forEach((address) => {
    ADDRESS_FIELD_NAMES.forEach((name) => {
      if (getAddressFieldError(address, name)) touched[addressTouchKey(address, name)] = true;
    });
  });

  return touched;
};

/* -------------------------------------------------------------------------- */
/* Draft persistence (survives the round trip to the "Add address" page)      */
/* -------------------------------------------------------------------------- */
const DRAFT_TTL_MS = 30 * 60 * 1000; // ignore drafts older than 30 minutes
const draftKey = (id) => `editDevoteeDraft:${id}`;

const readDraft = (id) => {
  try {
    const raw = sessionStorage.getItem(draftKey(id));
    if (!raw) return null;
    const draft = JSON.parse(raw);
    return Date.now() - draft.savedAt < DRAFT_TTL_MS ? draft : null;
  } catch (e) {
    return null;
  }
};

const writeDraft = (id, payload) => {
  try {
    sessionStorage.setItem(draftKey(id), JSON.stringify({ ...payload, savedAt: Date.now() }));
  } catch (e) {
    /* storage unavailable – we just lose the draft */
  }
};

const clearDraft = (id) => {
  try {
    sessionStorage.removeItem(draftKey(id));
  } catch (e) {
    /* ignore */
  }
};

/* -------------------------------------------------------------------------- */
/* Server <-> form mapping                                                    */
/* -------------------------------------------------------------------------- */
const DEVOTEE_FIELDS = [
  "devotee_id",
  "first_name",
  "middle_name",
  "last_name",
  "email",
  "gender",
  "birth_date",
  "anniversary_date",
  "comments",
  "picture",
];

const ADDRESS_FIELDS = [
  "devotee_address_id",
  "devotee_id",
  "title",
  "phone",
  "address_line_1",
  "address_line_2",
  "landmark",
  "area",
  "city",
  "state",
  "country",
  "pincode",
];

const toDevoteeForm = (data) =>
  Object.fromEntries(DEVOTEE_FIELDS.map((field) => [field, data[field]?.toString()]));

// Only keep fields the server actually returned (same behaviour as before).
const toAddressForm = (data) =>
  Object.fromEntries(ADDRESS_FIELDS.filter((field) => data[field]).map((f) => [f, `${data[f]}`]));

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */
const EditDevotee = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userData = useContext(UserContext);
  const { devotee_id } = useParams();

  // Read once on mount (the initializer is pure, so StrictMode is fine).
  const [draft] = useState(() => readDraft(devotee_id));
  const [devoteeData, setDevoteeData] = useState(null);
  const [devoteeAddress, setDevoteeAddress] = useState([]);
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);

  // Used to scroll to the first error when Save is clicked on an invalid form
  const formRef = useRef(null);
  const [scrollToErrorKey, setScrollToErrorKey] = useState(0);

  // The draft has been consumed into state; don't let it resurface later.
  useEffect(() => {
    clearDraft(devotee_id);
  }, [devotee_id]);

  useEffect(() => {
    if (!userData?.access_token) return undefined;
    let cancelled = false;

    const load = async () => {
      const [devoteeRes, addressRes] = await Promise.all([
        GetDevoteeDataAPI(devotee_id, userData.access_token, dispatch),
        GetDevoteeAddressAPI(devotee_id, userData.access_token, dispatch),
      ]);
      if (cancelled) return;

      if (!devoteeRes.is_success) {
        notify(devoteeRes.message);
        return;
      }

      const serverDevotee = toDevoteeForm(devoteeRes.data);
      // Unsaved edits win, but the picture is uploaded immediately so trust the server for it.
      const initialDevotee = draft
        ? { ...draft.devotee, picture: serverDevotee.picture }
        : serverDevotee;

      const serverAddresses = addressRes.is_success ? addressRes.data.list.map(toAddressForm) : [];
      const draftById = new Map((draft?.addresses ?? []).map((a) => [a.devotee_address_id, a]));
      // Existing addresses keep their unsaved edits; addresses that are new on the server
      // (i.e. the one just added) simply show up as-is.
      const initialAddresses = serverAddresses.map((a) => draftById.get(a.devotee_address_id) ?? a);

      setDevoteeData(initialDevotee);
      setDevoteeAddress(initialAddresses);
      // Show errors for values that are already invalid on load
      setTouched(getInitialTouched(initialDevotee, initialAddresses));
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [userData?.access_token, devotee_id]);

  // After the errors have been rendered, scroll to the first one
  useEffect(() => {
    if (!scrollToErrorKey) return;
    formRef.current
      ?.querySelector(".Mui-error")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [scrollToErrorKey]);

  /* ------------------------------- Validation ------------------------------ */
  const markTouched = (key) => setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));

  // Marks every validated devotee field and every address field as touched,
  // so all missing / wrong fields turn red at once
  const touchAllFields = () => {
    const all = {};
    VALIDATED_DEVOTEE_FIELDS.forEach((name) => {
      all[devoteeTouchKey(name)] = true;
    });
    devoteeAddress.forEach((address) => {
      ADDRESS_FIELD_NAMES.forEach((name) => {
        all[addressTouchKey(address, name)] = true;
      });
    });
    setTouched((prev) => ({ ...prev, ...all }));
  };

  // Real validation state: checked when Save is clicked
  const isFormValid =
    !!devoteeData &&
    VALIDATED_DEVOTEE_FIELDS.every((name) => !getDevoteeFieldError(devoteeData, name)) &&
    devoteeAddress.every((address) =>
      ADDRESS_FIELD_NAMES.every((name) => !getAddressFieldError(address, name))
    );

  // What the user sees: an error only appears once the field is touched
  // (blurred, already invalid when the page loaded, or Save was clicked)
  // { first_name: "First name is required", last_name: "", ... }
  const visibleDevoteeErrors = Object.fromEntries(
    VALIDATED_DEVOTEE_FIELDS.map((name) => [
      name,
      touched[devoteeTouchKey(name)] ? getDevoteeFieldError(devoteeData, name) : "",
    ])
  );

  // One entry per address, same order as `devoteeAddress`: [{ city: "...", phone: "..." }, ...]
  const visibleAddressErrors = devoteeAddress.map((address) =>
    Object.fromEntries(
      ADDRESS_FIELD_NAMES.map((name) => [
        name,
        touched[addressTouchKey(address, name)] ? getAddressFieldError(address, name) : "",
      ])
    )
  );

  /* -------------------------------- Handlers ------------------------------- */
  const handleDevoteeDataChange = (field, value) =>
    setDevoteeData((prev) => ({ ...prev, [field]: value }));

  const handleAddressChange = (index, field, value) => {
    const nextValue = field === "phone" ? sanitizePhone(value) : value;
    setDevoteeAddress((prev) =>
      prev.map((address, i) => (i === index ? { ...address, [field]: nextValue } : address))
    );
  };

  const handleDevoteeBlur = (field) => markTouched(devoteeTouchKey(field));

  const handleAddressBlur = (index, field) => {
    const address = devoteeAddress[index];
    if (address) markTouched(addressTouchKey(address, field));
  };

  const handleDeleteChanges = (address_id) =>
    setDevoteeAddress((prev) => prev.filter((item) => item.devotee_address_id !== address_id));

  // Picture is uploaded straight away, so refresh only that field (no page reload,
  // which would throw away unsaved edits).
  const handlePictureUploaded = async () => {
    const response = await GetDevoteeDataAPI(devotee_id, userData.access_token, dispatch);
    if (response.is_success) {
      handleDevoteeDataChange("picture", response.data.picture?.toString());
    }
  };

  const handleAddAddress = () => {
    writeDraft(devotee_id, { devotee: devoteeData, addresses: devoteeAddress });
    navigate(`/devotee/new_address/${devotee_id}`, {
      state: { returnTo: `/devotee/edit/${devotee_id}` },
    });
  };

  const handleEditSubmit = async () => {
    setLoading(true);
    try {
      const devoteeResponse = await EditDevoteDataAPI(devoteeData, userData.access_token);
      const addressResponses = await Promise.all(
        devoteeAddress.map((address) => EditDevoteeAddressAPI(address, userData.access_token))
      );

      const allSucceeded =
        devoteeResponse?.is_success && addressResponses.every((response) => response?.is_success);

      if (!allSucceeded) {
        notify("Something went wrong");
        return;
      }
      clearDraft(devotee_id);
      navigate(`/devotee/view/${devotee_id}`);
      notify("Devotee edited!", "success");
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    // Invalid form: show every error and scroll to the first one
    if (!isFormValid) {
      touchAllFields();
      setScrollToErrorKey((key) => key + 1);
      return;
    }

    await popUp(handleEditSubmit, undefined, saveMsg("devotee"));
  };

  return (
    <MDBox px={{ xs: 1, md: 3 }} pt={{ xs: 0, md: 1 }} pb={3}>
      <FullScreenLoader open={loading} />
      <SectionHeader
        icon="edit"
        label="EDIT DEVOTEE"
        startAction={
          <MDButton
            type="button"
            variant="outlined"
            color="primary"
            sx={ranuOutlinedButtonSx}
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
          >
            Back
          </MDButton>
        }
      />

      <form ref={formRef} noValidate autoComplete="off" onSubmit={handleFormSubmit}>
        {devoteeData ? (
          <>
            <SectionCard title="Details" subtitle="Fields marked * are required">
              <EditDevoteeDetails
                data={devoteeData}
                handleDataChange={handleDevoteeDataChange}
                onPictureUploaded={handlePictureUploaded}
                errors={visibleDevoteeErrors}
                requiredFields={REQUIRED_DEVOTEE_FIELDS}
                onFieldBlur={handleDevoteeBlur}
              />
            </SectionCard>

            <SectionCard title="Addresses" subtitle="Edit, delete or add an address">
              <MDBox display="flex" justifyContent={{ xs: "center", sm: "flex-end" }} mb={2}>
                <MDButton
                  type="button"
                  color="primary"
                  sx={ranuButtonSx}
                  onClick={handleAddAddress}
                >
                  Add New Address
                </MDButton>
              </MDBox>
              <EditDevoteeAddress
                devoteeAddress={devoteeAddress}
                handleDataChange={handleAddressChange}
                handleDeleteChanges={handleDeleteChanges}
                errors={visibleAddressErrors}
                onFieldBlur={handleAddressBlur}
              />
            </SectionCard>

            <MDBox display="flex" justifyContent={{ xs: "center", md: "flex-end" }}>
              <MDBox display="flex" gap={1.5} justifyContent={{ xs: "center", sm: "flex-end" }}>
                <MDButton
                  type="button"
                  variant="outlined"
                  color="primary"
                  sx={ranuOutlinedButtonSx}
                  disabled={loading}
                  onClick={() => {
                    clearDraft(devotee_id);
                    navigate(-1);
                  }}
                >
                  Cancel
                </MDButton>

                <MDButton color="primary" type="submit" sx={ranuButtonSx} disabled={loading}>
                  Save devotee
                </MDButton>
              </MDBox>
            </MDBox>
          </>
        ) : (
          <Loader />
        )}
      </form>
    </MDBox>
  );
};

export default EditDevotee;
